import { NavLink, useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import Logo from "../../assets/svg/admin-logo.svg";
import { onLogout } from "../../api-client/api_client";
import {
  ADMIN_ROOT_PATH,
  INTEGRATION_DASHBOARD_PATH,
  ALERTS_DASHBOARD_PATH,
  CANARY_TEST_DASHBOARD_PATH,
  CONDUCTOR_PATH,
  SYNC_PLAN_DASHBOARD_PATH,
  CODE_BLOCK_PROGRESS_PATH,
} from "../../router/RouterUtils";
import { LogOut, ChevronDown } from "lucide-react";
import {
  Badge,
  Button,
  ButtonVariant,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  TabsList,
} from "@merge-api/merge-javascript-shared";
import { getAlertCount, getCanaryTestCount } from "./utils/helpers";
import { useState } from "react";

const Nav = styled.nav.attrs({
  className: "px-8 py-3 border-gray-10 border-b-[1px] flex flex-row items-center bg-white z-50",
})`
  && {
    background: white;
    width: 100%;
    position: fixed;
  }
`;

function NavBar({ isAlertTabEnabled }: { isAlertTabEnabled: boolean | undefined }) {
  const history = useHistory();
  const location = useLocation();
  const logout = () =>
    onLogout(history, { onError: () => console.error("Failed to logout. Please try again.") });
  const ALERTS_TAB_VALUE = "alerts";
  const CANARY_TEST_TAB_VALUE = "canarytests";
  const SYNC_PLAN_TAB_VALUE = "syncplan";
  const CONDUCTOR_TAB_VALUE = "conductor";
  const [alertCount, setAlertCount] = useState<number | null>(null);
  const [canaryTestCount, setCanaryTestCount] = useState<number | null>(null);

  if (isAlertTabEnabled) {
    getAlertCount(setAlertCount);
  }
  getCanaryTestCount(setCanaryTestCount);

  const tabData = [
    {
      label: "Code blocks",
      value: "codeblocks",
      destination: CODE_BLOCK_PROGRESS_PATH,
    },
    {
      label: "Integrations",
      value: "integrations",
      destination: INTEGRATION_DASHBOARD_PATH,
    },
    ...(isAlertTabEnabled
      ? [
          {
            label: "Alerts",
            value: ALERTS_TAB_VALUE,
            destination: ALERTS_DASHBOARD_PATH,
          },
        ]
      : []),
    {
      label: "Canary tests",
      value: CANARY_TEST_TAB_VALUE,
      destination: CANARY_TEST_DASHBOARD_PATH,
    },
    {
      label: "Sync plans",
      value: SYNC_PLAN_TAB_VALUE,
      destination: SYNC_PLAN_DASHBOARD_PATH,
    },
    {
      label: "Conductor",
      value: CONDUCTOR_TAB_VALUE,
      destination: CONDUCTOR_PATH,
    },
  ];

  const linkData = [
    {
      onClick: () => window.open("https://app.merge.dev/"),
      title: "Dashboard",
    },
    {
      onClick: () => window.open("https://docs.merge.dev/"),
      title: "Docs",
    },
    {
      onClick: () => window.open("https://www.merge.dev"),
      title: "Landing",
    },
  ];

  const findCurrentTab = () => {
    // Conductor has subtabs and detail routes beneath it, so it prefix-matches
    // to keep the tab selected on any of them.
    const matchingTab = tabData.find((tab) =>
      tab.destination === CODE_BLOCK_PROGRESS_PATH || tab.destination === CONDUCTOR_PATH
        ? location.pathname.startsWith(tab.destination)
        : location.pathname === tab.destination
    );
    return matchingTab ? matchingTab.value : null;
  };

  const onTabChange = (_: any, newValue: string | number | null) => {
    if (typeof newValue === "string") {
      const selectedTab = tabData.find((tab) => tab.value === newValue);
      if (selectedTab && selectedTab.destination) {
        history.push(selectedTab.destination);
      }
    }
  };

  return (
    <Nav>
      <NavLink to={ADMIN_ROOT_PATH}>
        <img src={Logo} className="h-5" />
      </NavLink>

      <ul className="sub-navbar-nav-side m-0 p-0 space-x-1 flex-fill flex-row justify-end">
        <Tabs value={findCurrentTab()} variant="button" onChange={onTabChange}>
          <TabsList className="border-b-0">
            {tabData.map((tab) => (
              <Tab className="whitespace-nowrap" key={tab.value} value={tab.value}>
                {tab.label}
                {tab.value === ALERTS_TAB_VALUE ? (
                  <Badge className="ml-2" size="sm">
                    {alertCount}
                  </Badge>
                ) : tab.value === CANARY_TEST_TAB_VALUE ? (
                  <Badge className="ml-2" size="sm">
                    {canaryTestCount}
                  </Badge>
                ) : null}
              </Tab>
            ))}
          </TabsList>
        </Tabs>

        <Menu
          ButtonProps={{
            children: "External links",
            rightIcon: <ChevronDown size="16" />,
            variant: ButtonVariant.TextBlack,
            size: "md",
          }}
          menuPlacement="bottom-start"
          menuClassName="z-[1400]"
        >
          {linkData.map((link) => (
            <MenuItem onClick={link.onClick}>{link.title}</MenuItem>
          ))}
        </Menu>

        <Button
          onClick={logout}
          size="md"
          variant={ButtonVariant.TextBlack}
          rightIcon={<LogOut size="16" />}
        >
          Logout
        </Button>
      </ul>
    </Nav>
  );
}

export default NavBar;
